
import { defineComponent, computed } from 'vue';
import type { PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Integration } from '@/composables/useIntegrations';
import { pickCategoryName, pickLocalizedText } from '@/utils/localized-text';

interface CategoryData {
  id_category?: string | number;
  category_name: string;
}

export default defineComponent({
  name: 'IntegrationCard',

  props: {
    integration: {
      type: Object as PropType<Integration>,
      required: true,
    },
    isInstalled: {
      type: Boolean,
      default: false,
    },
    shopIntegrationStatus: {
      type: String,
      default: '',
    },
  },

  emits: ['install', 'cancel-installation', 'view-details'],

  setup(props) {
    const { t, locale } = useI18n();

    const partnerName = computed(() => {
      if (!props.integration.partners_data || props.integration.partners_data.length === 0) {
        return '';
      }
      return props.integration.partners_data[0].partner_name || '';
    });

    // Coming-soon integrations are visible in the marketplace but cannot be
    // installed yet. Surfaces a distinct badge + disabled CTA.
    const isComingSoon = computed(() => props.integration.status === 'soon');

    // Portée par boutique : intégration réservée par ShopiMind à une sélection de
    // boutiques. Signalée pour qu'une boutique autorisée sache qu'elle n'est pas publique.
    const isRestricted = computed(() => props.integration.visibility === 'restricted');

    // G14 — nom / description courte localisés (C10) avec repli legacy.
    const localizedName = computed(() => pickLocalizedText(
      (props.integration as any)?.name_i18n,
      locale.value,
    ) || props.integration.name || '');

    const localizedShortDescription = computed(() => pickLocalizedText(
      (props.integration as any)?.short_description_i18n,
      locale.value,
    ) || props.integration.short_description || '');

    // Parseur `category_name` + sélection localisée centralisés (G12).
    const getCategoryName = (category: CategoryData): string => pickCategoryName(category.category_name, locale.value);

    return {
      t,
      partnerName,
      isComingSoon,
      isRestricted,
      getCategoryName,
      localizedName,
      localizedShortDescription,
    };
  },
});
